import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import type {
  AvailabilityResponse,
  AvailabilityQuery,
  DateRangeAvailabilityResponse,
  PriceBreakdown,
  CreateBookingRequest,
  BookingResponse,
  BookingFilterQuery,
  PaginatedBookingsResponse,
  VendorEndSessionResponse,
} from '@lov/shared';

/**
 * Flat paginated shape returned by the Axios interceptor in apps/web.
 * The interceptor converts the nested backend envelope:
 *   { success: true, data: T[], meta: { pagination: { page, limit, total, totalPages } } }
 * into:
 *   { data: T[], total: number, page: number, limit: number, totalPages: number }
 */
export interface FlatPaginatedBookingsResponse {
  data: BookingResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================
// PUBLIC HOOKS
// ============================================

/**
 * Get availability slots for a venue on a specific date
 */
export function useVenueAvailability(
  venueId: string,
  params: AvailabilityQuery & { enablePolling?: boolean }
) {
  return useQuery<AvailabilityResponse>({
    queryKey: ['availability', venueId, params.date, params.inventoryItemId],
    queryFn: async () => {
      return validatedApi.get<AvailabilityResponse>(`/bookings/availability/${venueId}`, {
        params,
      });
    },
    enabled: !!venueId && !!params.date,
    staleTime: 10 * 1000, // Consider stale after 10 seconds (was 2 minutes)

    // Real-time polling
    refetchInterval: params.enablePolling ? 15000 : false,
    refetchIntervalInBackground: false, // Don't poll when tab is hidden
    refetchOnWindowFocus: true,
  });
}

/**
 * Get date range availability for a venue
 * Provides availability summary for multiple dates in a single request
 */
export function useDateRangeAvailability(venueId: string, startDate: string, endDate: string) {
  return useQuery<DateRangeAvailabilityResponse>({
    queryKey: ['availability', 'dateRange', venueId, startDate, endDate],
    queryFn: () =>
      validatedApi.get(`/bookings/availability/${venueId}/date-range`, {
        params: { startDate, endDate },
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
    enabled: !!venueId && !!startDate && !!endDate,
  });
}

/**
 * Get price quote for a booking
 */
export function usePriceQuote() {
  return useMutation<
    PriceBreakdown,
    Error,
    {
      venueId: string;
      startTime: string;
      endTime: string;
      guestCount?: number;
      inventoryItems?: { venueInventoryId: string; quantity: number }[];
      couponCode?: string;
    }
  >({
    mutationFn: async (params) => {
      return validatedApi.post<PriceBreakdown>('/bookings/price-quote', params);
    },
  });
}

// ============================================
// CUSTOMER HOOKS
// ============================================

/**
 * Create a new booking
 */
export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation<BookingResponse, Error, CreateBookingRequest>({
    mutationFn: async (bookingData) => {
      return validatedApi.post<BookingResponse>('/bookings', bookingData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
    },
  });
}

/**
 * Get current user's bookings
 */
export function useMyBookings(params: Partial<BookingFilterQuery> = {}) {
  return useQuery<PaginatedBookingsResponse>({
    queryKey: ['bookings', 'my', params],
    queryFn: async () => {
      return validatedApi.get<PaginatedBookingsResponse>('/bookings', { params });
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get single booking by ID
 */
export function useBooking(id: string) {
  return useQuery<BookingResponse>({
    queryKey: ['bookings', id],
    queryFn: async () => {
      return validatedApi.get<BookingResponse>(`/bookings/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Cancel a booking
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; reason?: string }>({
    mutationFn: async ({ id, reason }) => {
      // Cancel endpoint returns simple success message, not full BookingResponse
      // So we don't validate the response - just check for successful status code
      await validatedApi.put(`/bookings/${id}/cancel`, { reason });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
    },
  });
}

// ============================================
// VENDOR HOOKS
// ============================================

/**
 * Get bookings for a venue (vendor only)
 */
export function useVenueBookings(venueId: string, params: Partial<BookingFilterQuery> = {}) {
  return useQuery<PaginatedBookingsResponse>({
    queryKey: ['bookings', 'venue', venueId, params],
    queryFn: async () => {
      return validatedApi.get<PaginatedBookingsResponse>(`/bookings/venue/${venueId}`, { params });
    },
    enabled: !!venueId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Mark booking as completed
 */
export function useCompleteBooking() {
  const queryClient = useQueryClient();

  return useMutation<BookingResponse, Error, string>({
    mutationFn: async (id) => {
      return validatedApi.put<BookingResponse>(`/bookings/${id}/complete`);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', id] });
    },
  });
}

/**
 * Mark booking as no-show
 */
export function useMarkNoShow() {
  const queryClient = useQueryClient();

  return useMutation<BookingResponse, Error, string>({
    mutationFn: async (id) => {
      return validatedApi.put<BookingResponse>(`/bookings/${id}/no-show`);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', id] });
    },
  });
}

/**
 * End an active session (vendor-initiated)
 * Marks the booking as completed and invalidates active sessions + shift summary caches
 */
export function useVendorEndSession() {
  const queryClient = useQueryClient();
  return useMutation<
    VendorEndSessionResponse,
    Error,
    { bookingId: string; paymentMethod?: 'CASH' | 'UPI' | 'CARD' }
  >({
    mutationFn: ({ bookingId, paymentMethod }) =>
      validatedApi.post<VendorEndSessionResponse>(
        `/bookings/${bookingId}/vendor-end-session`,
        paymentMethod ? { paymentMethod } : {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings', 'active-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', 'shift-summary'] });
    },
  });
}

// useVendorCheckIn and useMarkAsPaid moved to separate files for better organization
// Import them from ./useVendorCheckIn and ./useMarkAsPaid

// ============================================
// ADMIN HOOKS
// ============================================

/**
 * Get all bookings (admin only)
 */
export function useAllBookings(params: Partial<BookingFilterQuery> = {}) {
  return useQuery<FlatPaginatedBookingsResponse>({
    queryKey: ['bookings', 'admin', 'all', params],
    queryFn: async () => {
      return validatedApi.get<FlatPaginatedBookingsResponse>('/bookings/admin/all', { params });
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    placeholderData: keepPreviousData,
  });
}

// ============================================
// PAYMENT HOOKS
// ============================================

export interface PaymentIntentResult {
  id: string;
  clientSecret: string;
  amount: number;
  currency: string;
  status: string;
  redirectData?: {
    url: string;
    params: Record<string, string>;
  };
}

/**
 * Initiate payment for a booking
 * Returns payment gateway URL to redirect user to
 */
export function useInitiatePayment() {
  return useMutation<PaymentIntentResult, Error, { bookingId: string }>({
    mutationFn: async ({ bookingId }) => {
      return validatedApi.post<PaymentIntentResult>('/payments/initiate', { bookingId });
    },
  });
}

/**
 * Get payment status
 */
export function usePaymentStatus(paymentId: string) {
  return useQuery<{ status: string; amount: number; transactionId?: string }>({
    queryKey: ['payment', 'status', paymentId],
    queryFn: async () => {
      return validatedApi.get(`/payments/${paymentId}/status`);
    },
    enabled: !!paymentId,
    refetchInterval: (query) => {
      // Poll every 3 seconds if payment is pending
      return query.state.data?.status === 'pending' ? 3000 : false;
    },
  });
}
